import type { AiRecommendationItem } from "@/hooks/services/pps/types/ai-recommendation.type";
import type {
  JobAssignment,
  MachineScheduleGantt,
  ScheduledJob,
  TechnicianScheduleGantt
} from "@/hooks/services/pps/handlers/schedule/job-schedule-types";

/** Group label for resources the backend could not match to Planner master data. */
export const UNMATCHED_SHOP_LABEL = "Not in Planner master data";

/**
 * The Gantt components key rows, selections and availability lookups by numeric id, but the
 * solver only gives us strings. Strings are mapped to ids like this:
 *  - matched to Planner master data → the real (positive) id, so availability bands still work
 *    and name variants of the same person/machine collapse into one row;
 *  - unmatched → a negative id derived from a hash of the normalized key. Negative values can never
 *    clash with database identities, and hashing (unlike a running counter) keeps the id stable when
 *    search/filter/date changes the set of rows, so the user's row selection survives refetches.
 */
export interface StringIdRegistry {
  resolve: (key: string) => number;
}

const fnv1a32 = (value: string): number => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
};

/**
 * Registers keys in sorted order so hash-collision resolution does not depend on row order.
 */
export const createStringIdRegistry = (keys: Iterable<string>): StringIdRegistry => {
  const idByKey = new Map<string, number>();
  const usedIds = new Set<number>();

  const assign = (key: string): number => {
    let id = -((fnv1a32(key) % 2_000_000_000) + 1);
    while (usedIds.has(id)) {
      id = id <= -2_000_000_000 ? -1 : id - 1;
    }
    usedIds.add(id);
    idByKey.set(key, id);
    return id;
  };

  Array.from(new Set(keys))
    .sort()
    .forEach(assign);

  return {
    resolve: (key) => idByKey.get(key) ?? assign(key)
  };
};

const resolveEntityId = (
  realId: number | null | undefined,
  key: string,
  registry: StringIdRegistry
): number => (realId != null && realId > 0 ? realId : registry.resolve(key));

interface ResolvedIds {
  jobId: number;
  technicianId: number | null;
  machineId: number | null;
}

/** Shared id resolution for the three views so the same string always lands on the same row id. */
export const createAiRecommendationIdResolver = (
  items: AiRecommendationItem[]
) => {
  const jobRegistry = createStringIdRegistry(items.map((i) => i.jobKey));
  const technicianRegistry = createStringIdRegistry(
    items.flatMap((i) => (i.technicianKey ? [i.technicianKey] : []))
  );
  const machineRegistry = createStringIdRegistry(
    items.flatMap((i) => (i.machineKey ? [i.machineKey] : []))
  );

  return (item: AiRecommendationItem): ResolvedIds => ({
    jobId: resolveEntityId(item.jobOrderId, item.jobKey, jobRegistry),
    technicianId: item.technicianKey
      ? resolveEntityId(item.technicianId, item.technicianKey, technicianRegistry)
      : null,
    machineId: item.machineKey
      ? resolveEntityId(item.machineId, item.machineKey, machineRegistry)
      : null
  });
};

/**
 * The solver has no part description; fall back to the serial number so the job card and
 * hover card still identify the physical component.
 */
const describePart = (item: AiRecommendationItem): string =>
  item.partDescription || (item.serialNo ? `S/N ${item.serialNo}` : "");

export const toAiAssignment = (
  item: AiRecommendationItem,
  ids: ResolvedIds,
  idPrefix: string
): JobAssignment => ({
  id: `${idPrefix}-${item.assignmentKey}`,
  assignmentId: item.assignmentKey,
  jobOrderNo: item.jobOrderNo,
  jobOrderId: ids.jobId,
  processId: 0,
  processCode: item.processSequence ?? 0,
  processDesc: item.processName || "",
  partNo: item.partNumber || "",
  partDescription: describePart(item),
  quantity: 0,
  standardHours: item.durationHours ?? 0,
  startDate: item.startDate || "",
  eddDate: item.eddDate || item.startDate || "",
  duration: item.durationHours ?? 0,
  technicianId: ids.technicianId,
  technicianName: item.technicianName,
  machineId: ids.machineId,
  machineName: item.machineName,
  statusId: 0,
  status: item.status || "recommended",
  priority: item.priority ?? 1,
  notes: null,
  customerName: item.customerName
});

/** Solver keys are unique per run, but guard against duplicates so React keys never collide. */
const dedupeAssignmentKeys = (
  items: AiRecommendationItem[]
): AiRecommendationItem[] => {
  const seen = new Map<string, number>();
  return items.map((item) => {
    const count = seen.get(item.assignmentKey) ?? 0;
    seen.set(item.assignmentKey, count + 1);
    return count === 0
      ? item
      : { ...item, assignmentKey: `${item.assignmentKey}#${count}` };
  });
};

const byName = <T>(getName: (row: T) => string) => (a: T, b: T) =>
  getName(a).localeCompare(getName(b), undefined, { sensitivity: "base" });

export interface AiRecommendationViews {
  jobs: ScheduledJob[];
  technicians: TechnicianScheduleGantt[];
  machines: MachineScheduleGantt[];
}

/**
 * Groups the flat solver rows into the job / technician / machine shapes the Gantt charts expect.
 * Rows without a worker (machine-only steps) or without a machine (labour-only steps) only appear in
 * the views where they have a resource.
 */
export const buildAiRecommendationViews = (
  rawItems: AiRecommendationItem[]
): AiRecommendationViews => {
  const items = dedupeAssignmentKeys(rawItems);
  const resolveIds = createAiRecommendationIdResolver(items);

  const jobs = new Map<number, ScheduledJob>();
  const technicians = new Map<number, TechnicianScheduleGantt>();
  const machines = new Map<number, MachineScheduleGantt>();

  items.forEach((item) => {
    const ids = resolveIds(item);
    const start = item.startDate || "";
    const end = item.eddDate || start;

    let job = jobs.get(ids.jobId);
    if (!job) {
      job = {
        id: ids.jobId,
        jobOrderNo: item.jobOrderNo,
        jobOrderId: ids.jobId,
        partNo: item.partNumber || "",
        partDescription: describePart(item),
        customerName: item.customerName,
        totalQuantity: 0,
        overallStartDate: start,
        overallEddDate: end,
        assignments: []
      };
      jobs.set(ids.jobId, job);
    }
    job.assignments.push(toAiAssignment(item, ids, "ai-job"));
    if (start && (!job.overallStartDate || start < job.overallStartDate)) {
      job.overallStartDate = start;
    }
    if (end && (!job.overallEddDate || end > job.overallEddDate)) {
      job.overallEddDate = end;
    }

    if (ids.technicianId != null) {
      let technician = technicians.get(ids.technicianId);
      if (!technician) {
        technician = {
          technicianId: ids.technicianId,
          technicianName: item.technicianName || item.technicianKey || "",
          shopName: item.technicianShopName || UNMATCHED_SHOP_LABEL,
          assignedJobs: []
        };
        technicians.set(ids.technicianId, technician);
      }
      technician.assignedJobs.push(toAiAssignment(item, ids, "ai-tech"));
    }

    if (ids.machineId != null) {
      let machine = machines.get(ids.machineId);
      if (!machine) {
        machine = {
          machineId: ids.machineId,
          machineName: item.machineName || item.machineKey || "",
          shopName: item.machineShopName || UNMATCHED_SHOP_LABEL,
          status: "available",
          assignedJobs: []
        };
        machines.set(ids.machineId, machine);
      }
      machine.assignedJobs.push(toAiAssignment(item, ids, "ai-machine"));
    }
  });

  return {
    // Jobs keep the backend order (which honours the user's sort); resources are listed by name.
    jobs: Array.from(jobs.values()),
    technicians: Array.from(technicians.values()).sort(
      byName((t) => t.technicianName)
    ),
    machines: Array.from(machines.values()).sort(byName((m) => m.machineName))
  };
};
